/**
 * Normalizes the fixed platform-usage fee (MID-747) as the API sends it.
 * The payload reaches the front camelized from `fixed_fee` and its amounts may
 * arrive as strings, as `null`, or missing altogether depending on the endpoint
 * and on whether the tenant has the fee configured. Parsing it once at the
 * boundary keeps every consumer from re-doing `?.amount || 0` and lets them treat
 * a falsy result as "this tenant charges no fee".
 *
 * A fee with no chargeable amount is not a fee, so it collapses to `null`: the
 * breakdown must not render a "platform usage $0" row. The tax, on the other
 * hand, is legitimately zero when the tenant is configured tax-free.
 * @param {Object} [fee] - Fee as sent by the API, per seat or as a purchase total.
 * @param {number|string} [fee.amount] - Fee charged.
 * @param {number|string} [fee.tax] - Tax over that amount, in the same scale.
 * @returns {{amount: number, tax: number}|null} Parsed fee, or null when there is nothing to charge.
 */
const parseFixedFee = (fee) => {
  const amount = Number(fee?.amount);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const tax = Number(fee?.tax);

  return {
    amount,
    tax: Number.isFinite(tax) && tax > 0 ? tax : 0,
  };
};

export default parseFixedFee;
